import { selectedBusinessWorkspace } from '../../../../shared/platform/business-workspaces';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { apiBaseOrigin } from '../../../../shared/config/api-origin';
import type {
  Assessment,
  AssessmentAuth,
  AssessmentOutcome,
  Definition,
  Evidence,
  ExactAsset,
  SynAssessmentAdapter,
  Workspace,
} from '../adapters/syn-assessment-adapter';
import { assessmentOutcomes, createSynAssessmentAdapter } from '../adapters/syn-assessment-adapter';
import './syn-assessment.css';

export function synAssessmentEnabled(
  dev = import.meta.env.DEV,
  location = typeof window === 'undefined' ? undefined : window.location,
) {
  if (!dev || !location || !['localhost', '127.0.0.1', '[::1]'].includes(location.hostname))
    return false;
  const query = new URLSearchParams(location.search);
  return (
    query.get('synLive') === '1' &&
    (selectedBusinessWorkspace()?.unified === true ||
      ['synMedia', 'synAssessment'].every((key) => query.get(key) === '1'))
  );
}
export const outcomeLabels: Record<AssessmentOutcome, string> = {
  SYN_ASSESSED_SUPPORTED: '合成测试评估：支持',
  SYN_ASSESSED_UNSUPPORTED: '合成测试评估：不支持',
  SYN_INCONCLUSIVE: '合成测试评估：证据不足',
  SYN_RETURNED_FOR_EVIDENCE: '合成测试评估：退回补证据',
};
const applicabilityLabels: Record<Assessment['applicability']['status'], string> = {
  CURRENT: '精确版本仍适用',
  STALE: '版本已变化 · 需要重新评估',
  DEFINITION_UNKNOWN: '能力定义未知',
  OWNER_UNAVAILABLE: '来源暂不可用',
  EVIDENCE_UNREADABLE: '证据不可读取',
  EVIDENCE_HASH_MISMATCH: '证据摘要不一致',
  SCOPE_DENIED: '无权读取当前范围',
};
function failureText(error: unknown) {
  return error instanceof Error ? error.message : '请求失败；结果未确认。';
}
function definitionKey(definition: Definition) {
  return JSON.stringify([
    definition.capabilityId,
    definition.definitionVersion,
    definition.definitionDigest,
  ]);
}
function exactMatch(record: Assessment, workspace: Workspace, definition: Definition) {
  return (
    record.asset.publishedVersion === workspace.asset.publishedVersion &&
    record.asset.digest === workspace.asset.digest &&
    definitionKey(record.definition) === definitionKey(definition)
  );
}
export function currentAssessment(workspace: Workspace, definition: Definition) {
  return workspace.assessments
    .filter((record) => exactMatch(record, workspace, definition))
    .sort((a, b) => b.submittedAt.localeCompare(a.submittedAt) || b.revision - a.revision)[0];
}
/** Lineage is selected by exact identities, never by display names or inferred capability. */
export function priorAssessment(workspace: Workspace, definition: Definition) {
  return workspace.assessments
    .filter(
      (record) =>
        record.asset.assetId === workspace.asset.assetId &&
        record.definition.capabilityId === definition.capabilityId,
    )
    .sort((a, b) => b.revision - a.revision)[0];
}
export function resultLabel(record?: Assessment) {
  if (!record) return '未评估';
  if (record.applicability.status !== 'CURRENT')
    return applicabilityLabels[record.applicability.status];
  return record.decision ? outcomeLabels[record.decision.outcome] : '未评估 · 等待独立评估';
}

/** Clear stale reads immediately on identity/retry changes; both success and failure are guarded. */
function useRead<T>(identity: string, read: (signal: AbortSignal) => Promise<T>) {
  const [attempt, setAttempt] = useState(0);
  const requestIdentity = `${identity}:${attempt}`;
  const [state, setState] = useState<{ identity: string; value?: T; error?: string } | null>(null);
  useEffect(() => {
    const controller = new AbortController();
    void read(controller.signal).then(
      (value) => {
        if (!controller.signal.aborted) setState({ identity: requestIdentity, value });
      },
      (error) => {
        if (!controller.signal.aborted)
          setState({ identity: requestIdentity, error: failureText(error) });
      },
    );
    return () => controller.abort();
  }, [requestIdentity, read]);
  const current = state?.identity === requestIdentity ? state : null;
  return {
    value: current?.value,
    error: current?.error,
    loading: !current,
    refresh: () => setAttempt((value) => value + 1),
  };
}
function useAlive() {
  const alive = useRef(true);
  useEffect(() => {
    alive.current = true;
    return () => {
      alive.current = false;
    };
  }, []);
  return alive;
}
function Audit({ value }: { value: unknown }) {
  const [notice, setNotice] = useState('');
  const text = JSON.stringify(value, null, 2);
  return (
    <details className="syn-assessment__audit">
      <summary>查看精确版本与审计详情</summary>
      <button
        type="button"
        onClick={() => {
          void navigator.clipboard.writeText(text).then(
            () => setNotice('审计详情已复制'),
            () => setNotice('复制不可用，请选择下方文本复制'),
          );
        }}
      >
        复制审计详情
      </button>
      {notice && <span role="status">{notice}</span>}
      <pre tabIndex={0}>{text}</pre>
    </details>
  );
}
function ScopeNote() {
  return (
    <p className="syn-assessment__notice">
      LOCAL SYN · 仅本机合成测试评估，不代表真实装备认证、运行可用性或任务派遣授权。
    </p>
  );
}
function EvidenceFacts({ evidence }: { evidence: Evidence }) {
  return (
    <>
      <strong>{evidence.name}</strong>
      <span>
        {evidence.mime} · {Math.ceil(evidence.size / 1024)} KB · 合成测试材料
      </span>
      <details className="syn-assessment__audit">
        <summary>材料来源与完整标识</summary>
        <p>{evidence.source}</p>
      </details>
      <span>展示依据：{evidence.basis}</span>
    </>
  );
}
function EvidenceDownload({
  port,
  assessmentId,
  evidence,
}: {
  port: SynAssessmentAdapter;
  assessmentId: string;
  evidence: Evidence;
}) {
  const [busy, setBusy] = useState(false),
    [error, setError] = useState('');
  const controller = useRef<AbortController | null>(null);
  useEffect(() => () => controller.current?.abort(), []);
  async function download() {
    controller.current?.abort();
    const request = new AbortController();
    controller.current = request;
    setBusy(true);
    setError('');
    try {
      const blob = await port.evidence(assessmentId, evidence, request.signal);
      if (request.signal.aborted) return;
      const url = URL.createObjectURL(blob),
        anchor = document.createElement('a');
      anchor.href = url;
      anchor.download = evidence.name;
      anchor.click();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch (error) {
      if (!request.signal.aborted) setError(failureText(error));
    } finally {
      if (!request.signal.aborted) setBusy(false);
    }
  }
  return (
    <li className="syn-assessment__evidence">
      <EvidenceFacts evidence={evidence} />
      <button type="button" disabled={busy} onClick={() => void download()}>
        {busy ? '正在核验 SHA-256…' : `核验并下载 ${evidence.name}`}
      </button>
      {error && <p role="alert">{error}</p>}
    </li>
  );
}

/** Five business blocks, identical for the submitter and the independent assessor. */
function AssessmentFacts({ record, port }: { record: Assessment; port: SynAssessmentAdapter }) {
  const decision = record.decision;
  return (
    <div className="syn-assessment__blocks" data-syn-assessment-id={record.assessmentId}>
      <section>
        <h4>1 · 厂家声明</h4>
        {record.product ? (
          <>
            <p>
              <strong>{record.product.modelName}</strong> · 型号发布版本{' '}
              {record.product.publishedVersion}
            </p>
            <p>{record.product.manufacturerClaims || '未提供厂家声明'}</p>
            <p>来源：{record.product.source}</p>
          </>
        ) : (
          <p>未关联产品型号（NOT_LINKED）</p>
        )}
        <small>产品型号声明不证明这台具体装备的能力。</small>
      </section>
      <section>
        <h4>2 · 资产配置</h4>
        <p>
          <strong>{record.asset.displayName}</strong> · 装备发布版本 {record.asset.publishedVersion}
        </p>
        <p>{record.asset.configurationDeclaration}</p>
        <p>用途：{record.asset.purpose}</p>
        <p>资料来源：{record.asset.sourceNote}</p>
        <p>
          机构范围：{record.asset.country} / {record.asset.agency}
        </p>
      </section>
      <section>
        <h4>3 · 能力定义与受控证据</h4>
        <p>
          <strong>{record.definition.displayName}</strong> · 定义版本{' '}
          {record.definition.definitionVersion}
        </p>
        <p>
          状态：{record.definition.definitionStatus} · 来源：{record.definition.originLabel}
        </p>
        <dl>
          <dt>测试方法与场景</dt>
          <dd>{record.method}</dd>
          <dt>实际观察结果</dt>
          <dd>{record.observations}</dd>
          <dt>使用限制</dt>
          <dd>{record.limitations}</dd>
        </dl>
        <ul>
          {record.evidence.map((evidence) => (
            <EvidenceDownload
              key={evidence.id}
              port={port}
              assessmentId={record.assessmentId}
              evidence={evidence}
            />
          ))}
        </ul>
      </section>
      <section>
        <h4>4 · SYN 独立技术评估</h4>
        <p className="syn-assessment__result">
          {decision ? outcomeLabels[decision.outcome] : '未评估 · 等待独立评估'}
        </p>
        {decision ? (
          <>
            <p>评估理由：{decision.reason}</p>
            <p>
              评估账号：{decision.assessorAccount} · {decision.decidedAt}
            </p>
          </>
        ) : (
          <p>资料审核或资料发布不会自动产生能力评估结论。</p>
        )}
        <p>
          发起账号：{record.submitterAccount} · {record.submittedAt} · 评估修订 {record.revision}
        </p>
      </section>
      <section className="syn-assessment__applicability">
        <h4>5 · 版本适用性</h4>
        <p data-syn-applicability={record.applicability.status}>
          {applicabilityLabels[record.applicability.status]}
        </p>
        <p>{record.applicability.reason}</p>
        {record.applicability.status !== 'CURRENT' && (
          <p>上述结论仅作为原始历史记录保留；当前版本不得继承该结论。</p>
        )}
        <small>“精确版本仍适用”只表示引用未变化。</small>
      </section>
      <Audit value={record} />
    </div>
  );
}

function SubmitForm({
  workspace,
  port,
  previous,
  onSaved,
}: {
  workspace: Workspace;
  port: SynAssessmentAdapter;
  previous: Assessment | null;
  onSaved: () => void;
}) {
  const [selected, setSelected] = useState(previous ? definitionKey(previous.definition) : '');
  const [evidenceIds, setEvidenceIds] = useState<string[]>(
    previous?.evidence.map((e) => e.id) ?? [],
  );
  const [method, setMethod] = useState(previous?.method ?? ''),
    [observations, setObservations] = useState(previous?.observations ?? ''),
    [limitations, setLimitations] = useState(previous?.limitations ?? '');
  const [busy, setBusy] = useState(false),
    [error, setError] = useState('');
  const alive = useAlive();
  const definition = workspace.definitions.find((item) => definitionKey(item) === selected);
  const predecessor = definition ? priorAssessment(workspace, definition) : undefined;
  const evidence = workspace.evidence.filter((item) => evidenceIds.includes(item.id));
  const complete =
    definition &&
    (!predecessor || predecessor.decision) &&
    evidence.length > 0 &&
    method.trim() &&
    observations.trim() &&
    limitations.trim();
  return (
    <form
      className="syn-assessment__form"
      onSubmit={(event) => {
        event.preventDefault();
        if (busy || !complete || !definition) return;
        setBusy(true);
        setError('');
        void port
          .submit({
            assetId: workspace.asset.assetId,
            publishedVersion: workspace.asset.publishedVersion,
            assetDigest: workspace.asset.digest,
            capabilityId: definition.capabilityId,
            definitionVersion: definition.definitionVersion,
            definitionDigest: definition.definitionDigest,
            evidence: evidence.map((item) => ({ id: item.id, sha256: item.sha256 })),
            method,
            observations,
            limitations,
            ...(predecessor ? { previousAssessmentId: predecessor.assessmentId } : {}),
          })
          .then(
            () => {
              if (alive.current) onSaved();
            },
            (error) => {
              if (alive.current) setError(failureText(error));
            },
          )
          .finally(() => {
            if (alive.current) setBusy(false);
          });
      }}
    >
      <h4>{previous ? '补充证据并创建新修订' : '发起合成评估'}</h4>
      {previous && (
        <p>
          原修订 {previous.revision} 保留不变。退回理由：{previous.decision?.reason}
        </p>
      )}
      <fieldset disabled={busy}>
        <label>
          选择精确能力定义
          <select
            aria-label="选择精确能力定义"
            required
            value={selected}
            onChange={(event) => setSelected(event.target.value)}
          >
            <option value="">请选择能力定义</option>
            {workspace.definitions.map((item) => (
              <option key={definitionKey(item)} value={definitionKey(item)}>
                {item.displayName} · v{item.definitionVersion} · {item.definitionStatus}
              </option>
            ))}
          </select>
        </label>
        {!workspace.definitions.length && <p>没有可读取的定义；不能凭名称推导能力。</p>}
        {predecessor && (
          <p>
            {predecessor.decision
              ? `本次将创建修订 ${predecessor.revision + 1}；原修订及决定保留，不继承原结论。`
              : '此装备与能力已有待评估记录，请先由独立评估员作出决定。'}
          </p>
        )}
        {definition && (
          <>
            <p>
              定义来源：{definition.originLabel} · {definition.capabilityType}
            </p>
            <Audit value={definition} />
          </>
        )}
        <fieldset>
          <legend>关联已发布版本的受控材料（至少一项）</legend>
          {workspace.evidence.map((item) => (
            <label key={item.id} className="syn-assessment__evidence-choice">
              <input
                type="checkbox"
                checked={evidenceIds.includes(item.id)}
                onChange={(event) =>
                  setEvidenceIds((ids) =>
                    event.target.checked ? [...ids, item.id] : ids.filter((id) => id !== item.id),
                  )
                }
              />
              <span>
                <EvidenceFacts evidence={item} />
              </span>
            </label>
          ))}
        </fieldset>
        {!workspace.evidence.length && (
          <p>
            此精确发布版本没有可评估材料。请使用原资料编辑与发布流程补充材料，再对新版本发起评估。
          </p>
        )}
        <label>
          测试方法与场景
          <textarea
            required
            maxLength={4000}
            value={method}
            onChange={(event) => setMethod(event.target.value)}
          />
        </label>
        <label>
          实际观察结果
          <textarea
            required
            maxLength={4000}
            value={observations}
            onChange={(event) => setObservations(event.target.value)}
          />
        </label>
        <label>
          材料使用限制
          <textarea
            required
            maxLength={4000}
            value={limitations}
            onChange={(event) => setLimitations(event.target.value)}
          />
        </label>
        <p>服务端记录发起人、机构范围和时间。提交后等待另一自然人的获准 SYN 评估。</p>
        <button type="submit" disabled={!complete}>
          {busy ? '提交中…' : previous ? '提交补证据新修订' : '提交合成评估'}
        </button>
      </fieldset>
      {error && <p role="alert">{error} 相同内容重试会使用原幂等键。</p>}
    </form>
  );
}

function AssetWorkspace({ auth, exact }: { auth: AssessmentAuth; exact: ExactAsset }) {
  const port = useMemo(
    () => createSynAssessmentAdapter(apiBaseOrigin(), auth),
    [auth.session, auth.csrf],
  );
  const read = useCallback(
    (signal: AbortSignal) => port.workspace(exact, signal),
    [port, exact.assetId, exact.publishedVersion, exact.digest],
  );
  const resource = useRead(JSON.stringify(exact), read);
  const [previous, setPrevious] = useState<Assessment | null>(null),
    [notice, setNotice] = useState('');
  const workspace = resource.value;
  return (
    <section className="syn-assessment" aria-label="05 能力评估">
      <div className="syn-assessment__heading">
        <div>
          <h3>能力与证据 · SYN 测试评估</h3>
          <p>装备精确发布版本 {exact.publishedVersion}</p>
        </div>
        <button
          type="button"
          disabled={resource.loading}
          onClick={() => {
            setNotice('');
            setPrevious(null);
            resource.refresh();
          }}
        >
          刷新能力与证据
        </button>
      </div>
      <ScopeNote />
      {notice && <p role="status">{notice}</p>}
      {resource.loading && <p role="status">正在读取精确发布版本、定义与评估记录…</p>}
      {resource.error && <p role="alert">{resource.error} 当前资料已清空，请刷新读取。</p>}
      {workspace && (
        <>
          <div className="syn-assessment__blocks">
            <section>
              <h4>厂家声明</h4>
              <p>{workspace.product?.modelName ?? '未关联产品型号（NOT_LINKED）'}</p>
              <p>{workspace.product?.manufacturerClaims ?? '无型号声明'}</p>
              {workspace.product && <p>来源：{workspace.product.source}</p>}
              <small>厂家型号声明不证明此装备的能力。</small>
            </section>
            <section>
              <h4>资产实际配置</h4>
              <p>
                <strong>{workspace.asset.displayName}</strong> · v{workspace.asset.publishedVersion}
              </p>
              <p>{workspace.asset.configurationDeclaration}</p>
              <p>用途：{workspace.asset.purpose}</p>
              <p>资料来源：{workspace.asset.sourceNote}</p>
            </section>
            <section>
              <h4>能力定义与证据</h4>
              <p>
                {workspace.definitions.length} 项来源定义 · {workspace.evidence.length}{' '}
                项已发布受控材料
              </p>
              <p>从下方选择精确定义及材料，提交测试方法和观察结果。</p>
            </section>
            <section>
              <h4>当前版本的 SYN 评估结果</h4>
              {workspace.definitions.length === 0 && <p>定义列表为空，当前能力状态未知。</p>}
              <ul className="syn-assessment__results">
                {workspace.definitions.map((definition) => {
                  const record = currentAssessment(workspace, definition);
                  return (
                    <li key={definitionKey(definition)}>
                      <strong>
                        {definition.displayName} · v{definition.definitionVersion}
                      </strong>
                      <span>{resultLabel(record)}</span>
                    </li>
                  );
                })}
              </ul>
            </section>
            <section>
              <h4>版本适用性</h4>
              <p>
                仅绑定装备发布 v{workspace.asset.publishedVersion}{' '}
                与每项精确定义；历史结论不会自动继承到新版本。
              </p>
              {workspace.assessments.some((item) => item.applicability.status === 'STALE') && (
                <p>存在版本已变化的历史评估，需要对当前版本重新评估。</p>
              )}
            </section>
          </div>
          <SubmitForm
            key={previous?.assessmentId ?? 'new'}
            workspace={workspace}
            port={port}
            previous={previous}
            onSaved={() => {
              setPrevious(null);
              setNotice('服务端已确认提交；正在重新读取。尚未形成独立评估结论。');
              resource.refresh();
            }}
          />
          {previous && (
            <button type="button" onClick={() => setPrevious(null)}>
              取消补证据，发起独立新评估
            </button>
          )}
          <section aria-label="不可变评估历史">
            <h4>不可变评估历史（{workspace.assessments.length}）</h4>
            {workspace.assessments.length === 0 && <p>未评估：没有合成技术评估记录。</p>}
            {workspace.assessments.map((record) => (
              <details key={record.assessmentId} className="syn-assessment__history">
                <summary>
                  {record.definition.displayName} · 装备 v{record.asset.publishedVersion} / 定义 v
                  {record.definition.definitionVersion} · 修订 {record.revision} ·{' '}
                  {resultLabel(record)}
                </summary>
                <AssessmentFacts record={record} port={port} />
                {record.decision?.outcome === 'SYN_RETURNED_FOR_EVIDENCE' && (
                  <button
                    type="button"
                    onClick={() => {
                      setPrevious(record);
                      setNotice('已载入退回评估。请在上方补证据表单提交新的不可变修订。');
                    }}
                  >
                    为此退回记录补证据
                  </button>
                )}
              </details>
            ))}
          </section>
          <Audit value={{ asset: workspace.asset, product: workspace.product }} />
        </>
      )}
    </section>
  );
}
export function SynAssetAssessment({
  auth,
  assetId,
  publications,
}: {
  auth: AssessmentAuth;
  assetId: string;
  publications: readonly { publishedVersion: number; digest: string }[];
}) {
  const [selected, setSelected] = useState<number | null>(null);
  if (!synAssessmentEnabled() || !auth.session) return null;
  const publication =
    publications.find((item) => item.publishedVersion === selected) ?? publications.at(-1);
  if (!publication)
    return (
      <section className="syn-assessment" aria-label="05 能力评估">
        <h3>能力与证据 · SYN 测试评估</h3>
        <ScopeNote />
        <p>本装备尚无已发布版本。请先完成已有资料审核与发布流程。</p>
      </section>
    );
  const exact = {
    assetId,
    publishedVersion: publication.publishedVersion,
    digest: publication.digest,
  };
  return (
    <>
      <label className="syn-assessment__version">
        查看评估所绑定的装备发布版本
        <select
          aria-label="查看评估所绑定的装备发布版本"
          value={publication.publishedVersion}
          onChange={(event) => setSelected(Number(event.target.value))}
        >
          {publications.map((item) => (
            <option key={item.publishedVersion} value={item.publishedVersion}>
              装备发布版本 {item.publishedVersion}
            </option>
          ))}
        </select>
      </label>
      <AssetWorkspace
        key={JSON.stringify([auth.session, auth.csrf, exact])}
        auth={auth}
        exact={exact}
      />
    </>
  );
}

function DecisionForm({
  record,
  port,
  onSaved,
  onReadFailure,
  draft,
  setDraft,
}: {
  record: Assessment;
  port: SynAssessmentAdapter;
  onSaved: () => void;
  onReadFailure: (message: string) => void;
  draft: { outcome: AssessmentOutcome; reason: string };
  setDraft: (draft: { outcome: AssessmentOutcome; reason: string }) => void;
}) {
  const { outcome, reason } = draft;
  const [busy, setBusy] = useState(false);
  const alive = useAlive();
  if (record.decision) return <p>此修订已有不可变决定。再次评估必须新建记录。</p>;
  return (
    <form
      className="syn-assessment__form"
      onSubmit={(event) => {
        event.preventDefault();
        if (busy || !reason.trim() || record.applicability.status !== 'CURRENT') return;
        setBusy(true);
        void port
          .decide(record.assessmentId, { expectedDigest: record.digest, outcome, reason })
          .then(
            () => {
              if (alive.current) onSaved();
            },
            (error) => {
              if (alive.current) {
                onReadFailure(failureText(error));
              }
            },
          )
          .finally(() => {
            if (alive.current) setBusy(false);
          });
      }}
    >
      <h4>记录独立 SYN 技术评估决定</h4>
      <p>仅获准的 ADMIN 测试账号可决定。服务端核验明确 SYN 许可、国家与机构范围及不同自然人。</p>
      <fieldset disabled={busy || record.applicability.status !== 'CURRENT'}>
        <label>
          合成评估结论
          <select
            aria-label="合成评估结论"
            value={outcome}
            onChange={(event) =>
              setDraft({ ...draft, outcome: event.target.value as AssessmentOutcome })
            }
          >
            {assessmentOutcomes.map((value) => (
              <option key={value} value={value}>
                {outcomeLabels[value]}
              </option>
            ))}
          </select>
        </label>
        <label>
          评估理由
          <textarea
            required
            maxLength={4000}
            value={reason}
            onChange={(event) => setDraft({ ...draft, reason: event.target.value })}
          />
        </label>
        <button type="submit" disabled={!reason.trim()}>
          {busy ? '提交决定中…' : '保存 SYN 技术评估决定'}
        </button>
      </fieldset>
      {record.applicability.status !== 'CURRENT' && <p>当前来源或版本不适用，无法记录决定。</p>}
    </form>
  );
}
function AssessmentDetail({
  id,
  port,
  onDecided,
}: {
  id: string;
  port: SynAssessmentAdapter;
  onDecided: () => void;
}) {
  const read = useCallback((signal: AbortSignal) => port.read(id, signal), [id, port]);
  const resource = useRead(id, read);
  const [draft, setDraft] = useState<{ outcome: AssessmentOutcome; reason: string }>({
    outcome: 'SYN_INCONCLUSIVE',
    reason: '',
  });
  const [commandError, setCommandError] = useState('');
  return (
    <section aria-label="SYN 评估精确详情">
      <button type="button" disabled={resource.loading} onClick={resource.refresh}>
        重新读取评估详情
      </button>
      {resource.loading && <p role="status">正在读取不可变评估与当前适用性…</p>}
      {resource.error && <p role="alert">{resource.error} 先前详情已清空。</p>}
      {commandError && (
        <p role="alert">
          {commandError} 决定未确认，先前详情已清空并重新读取。原内容重试沿用原幂等键。
        </p>
      )}
      {resource.value && (
        <>
          <AssessmentFacts record={resource.value} port={port} />
          <DecisionForm
            key={resource.value.digest}
            record={resource.value}
            port={port}
            onSaved={onDecided}
            draft={draft}
            setDraft={setDraft}
            onReadFailure={(message) => {
              setCommandError(message);
              resource.refresh();
            }}
          />
        </>
      )}
    </section>
  );
}
function AssessmentQueue({ auth }: { auth: AssessmentAuth }) {
  const port = useMemo(
    () => createSynAssessmentAdapter(apiBaseOrigin(), auth),
    [auth.session, auth.csrf],
  );
  const read = useCallback((signal: AbortSignal) => port.list(signal), [port]);
  const resource = useRead(auth.session, read);
  const [selected, setSelected] = useState(''),
    [notice, setNotice] = useState('');
  return (
    <section className="syn-assessment" aria-label="08 能力评估（SYN）">
      <div className="syn-assessment__heading">
        <div>
          <h3>能力评估（SYN）</h3>
          <p>独立技术评估队列 · 复用当前 08 合成会话</p>
        </div>
        <button
          type="button"
          disabled={resource.loading}
          onClick={() => {
            setSelected('');
            setNotice('');
            resource.refresh();
          }}
        >
          刷新 SYN 评估队列
        </button>
      </div>
      <ScopeNote />
      <p>这里的决定仅记录合成测试结论，与下方装备资料审核和发布决定分开。</p>
      {notice && <p role="status">{notice}</p>}
      {resource.loading && <p role="status">正在读取当前范围的评估队列…</p>}
      {resource.error && <p role="alert">{resource.error} 队列和详情已清空。</p>}
      {resource.value && (
        <div className="syn-assessment__queue">
          <nav aria-label="SYN 能力评估申请">
            {resource.value.length === 0 && <p>当前范围没有能力评估申请。</p>}
            {resource.value.map((record) => (
              <button
                type="button"
                key={record.assessmentId}
                aria-pressed={selected === record.assessmentId}
                onClick={() => {
                  setSelected(record.assessmentId);
                  setNotice('');
                }}
              >
                <strong>{record.asset.displayName}</strong>
                <span>
                  {record.definition.displayName} · 修订 {record.revision}
                </span>
                <span>{resultLabel(record)}</span>
                <small>{record.submittedAt}</small>
              </button>
            ))}
          </nav>
          <div>
            {selected && resource.value.some((record) => record.assessmentId === selected) ? (
              <AssessmentDetail
                key={selected}
                id={selected}
                port={port}
                onDecided={() => {
                  setNotice('服务端已确认 SYN 决定；刷新队列后可选择原记录精确读回。');
                  setSelected('');
                  resource.refresh();
                }}
              />
            ) : (
              <p>选择一条申请，读取精确资产、定义、证据和限制。</p>
            )}
          </div>
        </div>
      )}
    </section>
  );
}
export function SynAssessmentQueue({ auth }: { auth: AssessmentAuth }) {
  if (!synAssessmentEnabled() || !auth.session) return null;
  return <AssessmentQueue key={JSON.stringify([auth.session, auth.csrf])} auth={auth} />;
}
